import { Typography, Menu, MenuItem, Stack, Chip } from '@vapor/material'
import { Tooltip } from '@mui/material'
import { useCallback, useRef, useState } from 'react'
import { ReservedResourcePhase, useReservationStatesQuery, useReservationUpdateMutation } from 'src/api/api.generated'
import { ReservationHeaderComponentsCommonProps } from './header'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import { useMemo } from 'react'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { ReservationState, ReservationStateIcon } from './header-components'
import useUpdateReservationState from '../api/update-state.hook'
import { useTranslation } from 'react-i18next'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { compareDates } from 'src/utils/date'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { HicIconButton } from 'src/app/components/hic-vapor/button/icon-button'
import { faCalendarPlus } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DatePicker } from '@mui/x-date-pickers-pro'

export function State({ reservation, readOnly }: ReservationHeaderComponentsCommonProps) {
  const tenantId = useTenant().id
  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()
  const updateState = useUpdateReservationState()
  const [updateReservationMutation] = useReservationUpdateMutation()
  const ref = useRef(null)
  const changeExpirationRef = useRef<HTMLDivElement | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const [openDatePicker, setOpenDatePicker] = useState(false)
  const openPopover = useCallback((element: HTMLDivElement) => {
    setAnchorEl(element)
  }, [])
  const { t } = useTranslation()
  const formatDate = useFormatDate()
  const hotelDate = useHotelDate()

  const closePopover = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const onItemClick = useCallback(
    async (stateId: string) => {
      const result = await updateState(reservation.id, stateId)
      if (result.type === 'SUCCESS') {
        showSuccess(t('pms.booking.reservation.state_updated'))
        closePopover()
      } else {
        showError(result.errors)
      }
    },
    [updateState, reservation.id],
  )

  async function onChangeDatePicker(date: Date | null | undefined) {
    if (reservation.minStart && date && compareDates(reservation.minStart, date) < 0) {
      return showError(t('pms.booking.reservation.expiration_before_start'))
    }
    const result = await updateReservationMutation({
      variables: {
        id: reservation.id,
        input: {
          tenantId: tenantId,
          optionExpiration: date,
        },
      },
      refetchQueries: ['ReservationSearch'],
    })

    if (result.errors) {
      showError(result.errors)
    } else {
      setOpenDatePicker(false)
      showSuccess(t('pms.booking.reservation.expiration_updated'))
    }
  }

  const { data } = useReservationStatesQuery({ variables: { tenantId: tenantId } })
  const reservationStates = useMemo(() => data?.reservationStates?.slice().sort((a, b) => a.order - b.order) || [], [data])
  const disabled = useMemo(() => reservation.reservedResources?.some((rr) => rr.phase !== ReservedResourcePhase.RESERVATION), [reservation])

  return (
    <div ref={ref} style={{ pointerEvents: readOnly ? 'none' : undefined }}>
      <Stack alignItems="end">
        <Tooltip title={disabled ? t('pms.booking.reservation.state_tooltip_disabled') : t('pms.booking.reservation.state_tooltip')} arrow>
          <span style={{ display: 'block' }}>
            <Stack direction="row" alignItems="center">
              <Chip //
                onClick={!disabled ? (event) => openPopover(event.currentTarget) : undefined}
                size="small"
                sx={{ borderColor: `${reservation.reservationState.color}!important`, backgroundColor: 'transparent!important' }}
                icon={<ReservationStateIcon reservationState={reservation.reservationState} />}
                label={
                  <Typography //
                    noWrap
                    variant="body700"
                    sx={{ height: '100%', width: '100%', textAlign: 'left', color: reservation.reservationState.color }}
                  >
                    {reservation.reservationState.name}
                  </Typography>
                }
              />
              {reservation.reservationState.optioned && (
                <span ref={changeExpirationRef}>
                  <HicIconButton
                    disabled={disabled}
                    sx={{ height: 'unset!important', minHeight: '0!important', width: 'unset!important', minWidth: '0!important' }}
                    onClick={() => {
                      setOpenDatePicker(true)
                    }}
                  >
                    <FontAwesomeIcon icon={faCalendarPlus} />
                  </HicIconButton>
                </span>
              )}
            </Stack>
          </span>
        </Tooltip>
        {reservation.reservationState.optioned && (
          <DatePicker
            value={reservation.optionExpiration}
            open={openDatePicker}
            onClose={() => {
              setOpenDatePicker(false)
            }}
            maxDate={reservation.minStart}
            minDate={hotelDate}
            onChange={onChangeDatePicker}
            slots={{
              textField: () => (
                <Typography variant="caption">
                  {t('pms.booking.reservation.expiration')}{' '}
                  {reservation.optionExpiration ? (
                    <span style={{ color: compareDates(hotelDate, reservation.optionExpiration) >= 0 ? 'var(--error)' : undefined }}>{formatDate(reservation.optionExpiration, 'iii PPP')}</span>
                  ) : (
                    t('pms.booking.reservation.no_expiration')
                  )}
                </Typography>
              ),
            }}
            slotProps={{
              popper: {
                anchorEl: changeExpirationRef?.current,
              },
            }}
          />
        )}
      </Stack>
      <Menu
        open={anchorEl ? true : false}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        MenuListProps={{
          style: { minWidth: (ref as any)?.current?.offsetWidth },
        }}
      >
        {reservationStates.map((state) => (
          <MenuItem //
            key={state.id}
            onClick={() => onItemClick(state.id)}
            selected={state.id === reservation.reservationState.id}
          >
            <ReservationState reservationState={state} />
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}
